// Tegner en GT-spec på et 1920×1080-lærred. Positioner, farver og fonte
// kommer fra specen; animationer styres af StagePlayer.

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { GtElement, GtSpec } from "./spec.js";
import type { StagePlayer } from "./player.js";

// vMix' standard når FontSize mangler (målt på R3-boksen i UR).
const DEFAULT_FONT_SIZE = 36;

const ASSETS = "/gfx-assets/";

function shadow(blur: number | null): string | undefined {
  // vMix' Shadow-effekt: blød, mørk skygge rundt om objektet.
  return blur ? `drop-shadow(0 0 ${blur / 2}px rgba(0, 0, 0, 0.85))` : undefined;
}

function feather(f: number[] | undefined): CSSProperties {
  if (!f) return {};
  const [l = 0, t = 0, r = 0, b = 0] = f;
  const mask =
    `linear-gradient(to right, transparent, #000 ${l}px, #000 calc(100% - ${r}px), transparent), ` +
    `linear-gradient(to bottom, transparent, #000 ${t}px, #000 calc(100% - ${b}px), transparent)`;
  return { WebkitMaskImage: mask, maskImage: mask, WebkitMaskComposite: "source-in", maskComposite: "intersect" };
}

const JUSTIFY = { left: "flex-start", center: "center", right: "flex-end" } as const;
const VALIGN = { top: "flex-start", center: "center", bottom: "flex-end" } as const;

function Node({ el, text, image, nodeRef, textRef }: {
  el: GtElement;
  text: string | undefined;
  image: string | undefined; // billede fra data (fx fighterfoto) i stedet for specens
  nodeRef: (n: HTMLElement | null) => void;
  textRef: (n: HTMLElement | null) => void;
}) {
  const box: CSSProperties = {
    position: "absolute",
    left: el.x,
    top: el.y,
    width: el.w,
    height: el.h,
    filter: shadow(el.shadow),
    visibility: "hidden",
    ...feather(el.feather),
  };
  switch (el.type) {
    case "rect":
      return (
        <div
          ref={nodeRef}
          style={{
            ...box,
            background: el.fill,
            borderRadius: el.radius ? `${el.radius[0]}px / ${el.radius[1] ?? el.radius[0]}px` : undefined,
          }}
        />
      );
    case "ellipse":
      return <div ref={nodeRef} style={{ ...box, background: el.fill, borderRadius: "50%" }} />;
    case "image": {
      const src = image ?? (el.src ? ASSETS + el.src : "");
      if (!src) return <div ref={nodeRef} style={{ ...box, display: "none" }} />;
      return <img ref={nodeRef} src={src} alt="" style={{ ...box, objectFit: "contain" }} />;
    }
    case "text":
      return (
        <div
          ref={nodeRef}
          style={{
            ...box,
            display: "flex",
            alignItems: VALIGN[el.valign],
            justifyContent: JUSTIFY[el.align],
            textAlign: el.align,
            color: el.color,
            fontFamily: `"${el.font}", sans-serif`,
            fontSize: el.size ?? DEFAULT_FONT_SIZE,
            lineHeight: "normal",
            textTransform: "uppercase",
            whiteSpace: el.wrap ? "pre-wrap" : "pre",
          }}
        >
          <span ref={textRef}>{text ?? el.text}</span>
        </div>
      );
  }
}

export function Stage({
  spec,
  player,
  texts,
  visible,
  images = {},
  children,
}: {
  spec: GtSpec;
  player: StagePlayer;
  texts: Record<string, string>; // elementnavn → tekst
  visible: Record<string, boolean>; // elementnavn → synlighed fra data
  images?: Record<string, string>; // elementnavn → billed-URL fra data
  children?: ReactNode; // ekstra indhold i første lag, så det følger lagets animationer
}) {
  const [fontsReady, setFontsReady] = useState(document.fonts.status === "loaded");
  useEffect(() => {
    document.fonts.ready.then(() => setFontsReady(true));
  }, []);

  player.visibleOverrides = visible;
  useLayoutEffect(() => player.apply());

  // AutoSize="Shrink": skru ned for tekststørrelsen til teksten kan være der.
  const spans = useRef(new Map<string, HTMLElement>());
  const textKey = JSON.stringify(texts);
  useLayoutEffect(() => {
    for (const layer of spec.layers) {
      for (const el of layer.elements) {
        if (el.type !== "text" || !el.shrink) continue;
        const span = spans.current.get(el.name);
        const box = span?.parentElement;
        if (!span || !box) continue;
        let size = el.size ?? DEFAULT_FONT_SIZE;
        box.style.fontSize = `${size}px`;
        while (size > 8 && span.offsetWidth > el.w) box.style.fontSize = `${--size}px`;
      }
    }
  }, [spec, textKey, fontsReady]);

  return (
    <div
      ref={player.setRoot}
      style={{ position: "absolute", left: 0, top: 0, width: spec.width, height: spec.height, visibility: "hidden" }}
    >
      {spec.layers.map((layer, li) => (
        <div
          key={layer.name}
          ref={player.ref(layer.name)}
          style={{ position: "absolute", left: 0, top: 0, width: layer.w, height: layer.h }}
        >
          {layer.elements.map((el, i) => (
            <Node
              key={`${el.name}-${i}`}
              el={el}
              text={texts[el.name]}
              image={images[el.name]}
              nodeRef={player.ref(el.name)}
              textRef={(n) => (n ? spans.current.set(el.name, n) : spans.current.delete(el.name))}
            />
          ))}
          {li === 0 && children}
        </div>
      ))}
    </div>
  );
}
